import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { AdminEvent, RoleBucket, VolunteerRole } from "../../../shared/types";
import {
  adminEventRoster,
  adminGetScheduleOverrides,
  adminListEvents,
  adminListVolunteers,
  adminSaveScheduleOverrides,
} from "../../api";
import { formatDate, monthLabel, weekdayOf } from "../../lib/hours";

// 排班分頁：純讀取。依「填單送出時間」先到先得排序，名額內＝入選、名額外＝遞補 N。
// 不動志工填單與場次資料。手動微調 / 匯入的覆寫存在 settings.schedule_overrides，
// 所有管理員共用；「重新整理」只更新填單資料、不清掉覆寫。「全部清除」才清空。

type RosterRow = {
  id: number;
  name: string;
  primaryEmail: string;
  phone: string;
  status: string;
  totalHours: number;
  bucket: RoleBucket;
  waitlistPosition: number;
};

const BUCKET_LABEL: Record<RoleBucket, string> = {
  seed: "英文種子教師",
  activity: "活動志工",
};

const LOCATION_TABS: Array<{ key: string; label: string; match: (e: AdminEvent) => boolean }> = [
  { key: "all", label: "全部場次", match: () => true },
  { key: "daoxiang", label: "稻香國小", match: (e) => e.locationKey === "daoxiang" },
  { key: "jian", label: "吉安國小", match: (e) => e.locationKey === "jian" },
  { key: "charity", label: "星星國公益活動", match: (e) => e.category === "charity" },
];

type OverrideMap = Record<string, "in" | "wait">;

const MIN_HOURS = 4; // 完成計畫門檻，與系統設定一致

export default function SchedulingTab() {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [rosters, setRosters] = useState<Record<number, RosterRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [tab, setTab] = useState("all");
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [importText, setImportText] = useState("");
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [rolesByName, setRolesByName] = useState<Record<string, VolunteerRole[]>>({});
  const [expandedVol, setExpandedVol] = useState<string | null>(null);
  const [showGaps, setShowGaps] = useState(false);
  const [showHours, setShowHours] = useState(false);
  const [expandedGap, setExpandedGap] = useState<number | null>(null);
  const [highlightedEvent, setHighlightedEvent] = useState<number | null>(null);
  const [view, setView] = useState<"overview" | "sessions">("overview");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<"idle" | "saving" | "error">("idle");

  const hydrated = useRef(false); // 首次從伺服器載入 overrides 前，不要往回存
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [{ events: evs }, vList, ov] = await Promise.all([
        adminListEvents(),
        adminListVolunteers({}).catch(() => ({ volunteers: [] as Array<{ name: string; roles: VolunteerRole[] }> })),
        adminGetScheduleOverrides().catch(() => ({ at: null, data: {} as Record<string, "in" | "wait"> })),
      ]);
      const pairs = await Promise.all(
        evs.map(async (e) => [e.id, (await adminEventRoster(e.id)).roster] as const),
      );
      setEvents(evs);
      setRosters(Object.fromEntries(pairs));
      setRolesByName(
        Object.fromEntries(vList.volunteers.map((v) => [v.name.trim(), v.roles])),
      );
      hydrated.current = false;
      setOverrides(ov.data ?? {});
      setSavedAt(ov.at ?? null);
      // 下一輪 microtask 再開放自動存，避免把剛載入的值又存一次
      setTimeout(() => {
        hydrated.current = true;
      }, 0);
      setLoadedAt(new Date());
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 手動調整 / 匯入後，自動存回伺服器（所有管理員共用），debounce 700ms。
  // 空的覆寫不自動寫（避免在原本就沒有覆寫時反覆存一個空 blob）；要清空共用覆寫請按「全部清除」。
  useEffect(() => {
    if (!hydrated.current) return;
    if (Object.keys(overrides).length === 0) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSyncState("saving");
      try {
        const res = await adminSaveScheduleOverrides(overrides);
        setSavedAt(res.at);
        setSyncState("idle");
      } catch {
        setSyncState("error");
      }
    }, 700);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrides]);

  const activeFilter = LOCATION_TABS.find((t) => t.key === tab) ?? LOCATION_TABS[0];

  // 從「缺人清單」跳到「各場次名單」檢視的對應那一場（必要時先切篩選、切檢視、再捲動、再閃一下）
  function jumpToEvent(eventId: number) {
    const ev = events.find((x) => x.id === eventId);
    if (ev && !activeFilter.match(ev)) setTab("all");
    setView("sessions");
    setHighlightedEvent(eventId);
    window.setTimeout(() => {
      document
        .getElementById(`sched-event-${eventId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    window.setTimeout(() => setHighlightedEvent(null), 3000);
  }

  const shownEvents = useMemo(() => {
    return [...events]
      .filter(activeFilter.match)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  }, [events, tab]);

  const isDual = (name: string) => {
    const rs = rolesByName[name.trim()] ?? [];
    return rs.includes("english_seed") && rs.includes("activity");
  };

  // 依 bucket 把某場次的名單切成兩區，套用指定的覆寫表判定入選 / 遞補。
  //  種子區：roster 中 bucket==='seed' 的人（含雙身分——bucketOf 對雙身分回傳 seed）。
  //  志工區：roster 中 bucket==='activity' 的純志工 ＋ 另外把「雙身分」的人也列進來（crossListed）。
  //  覆寫 key = `${eventId}:${volunteerId}:${bucket}`，種子區與志工區各自獨立。
  function splitByBucketWith(
    eventId: number,
    cap: number | null,
    bucket: RoleBucket,
    ovr: OverrideMap,
  ) {
    const roster = rosters[eventId] ?? [];
    const base =
      bucket === "seed"
        ? roster.filter((r) => r.bucket === "seed").map((r) => ({ r, crossListed: false }))
        : [
            ...roster.filter((r) => r.bucket === "activity").map((r) => ({ r, crossListed: false })),
            ...roster
              .filter((r) => r.bucket === "seed" && isDual(r.name))
              .map((r) => ({ r, crossListed: true })),
          ];
    return base.map(({ r, crossListed }, idx) => {
      const key = `${eventId}:${r.id}:${bucket}`;
      const ov = ovr[key];
      const baseIn = crossListed ? false : r.waitlistPosition === 0;
      const isIn = ov ? ov === "in" : baseIn;
      return {
        ...r,
        crossListed,
        seq: idx + 1,
        isIn,
        overridden: !!ov && ov !== (baseIn ? "in" : "wait"),
        capLabel: cap == null ? "不限" : String(cap),
      };
    });
  }
  // 給定一份覆寫表，算出每個人的「已排時數」（雙身分一場只算一次）。
  function hoursMapFor(ovr: OverrideMap): Map<string, number> {
    const m = new Map<string, number>();
    for (const e of events) {
      const perVol = new Map<number, { name: string; anyIn: boolean }>();
      for (const bucket of ["seed", "activity"] as RoleBucket[]) {
        const cap = bucket === "seed" ? e.capacitySeed : e.capacityActivity;
        for (const r of splitByBucketWith(e.id, cap, bucket, ovr)) {
          const p = perVol.get(r.id) ?? { name: r.name, anyIn: false };
          if (r.isIn) p.anyIn = true;
          perVol.set(r.id, p);
        }
      }
      for (const [, p] of perVol) {
        if (p.anyIn) m.set(p.name, (m.get(p.name) ?? 0) + e.creditHours);
      }
    }
    return m;
  }

  // 切換某人在某區（種子 / 志工）的入選狀態（相對目前顯示的狀態）。
  // 雙身分在一區排入時，另一區自動退為遞補（不能同時上兩個角色）。
  function toggle(eventId: number, volunteerId: number, currentIsIn: boolean, bucket: RoleBucket) {
    const key = `${eventId}:${volunteerId}:${bucket}`;
    const other: RoleBucket = bucket === "seed" ? "activity" : "seed";
    const otherKey = `${eventId}:${volunteerId}:${other}`;
    const nextVal: "in" | "wait" = currentIsIn ? "wait" : "in";
    setOverrides((o) => {
      const copy = { ...o, [key]: nextVal };
      if (nextVal === "in") copy[otherKey] = "wait";
      return copy;
    });
  }

  // 姓名 → volunteerId（取自各場次名單）
  const nameToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const rows of Object.values(rosters)) {
      for (const r of rows) m.set(r.name.trim(), r.id);
    }
    return m;
  }, [rosters]);

  // 手動匯入 / 覆寫排班：貼上「姓名, 日期(YYYY-MM-DD 可留空), 場次關鍵字, 入選/遞補」
  // 或直接貼回本頁匯出的排班 CSV。改的是共用的排班覆寫，不動志工填單與場次資料。
  function applyImport() {
    const lines = importText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) {
      setImportMsg("沒有內容");
      return;
    }
    // 判斷是否為匯出 CSV 的表頭（含「身分別」欄可指定種子區 / 志工區）
    let dateIdx = 1;
    let eventIdx = 2;
    let resultIdx = 3;
    let nameIdx = 0;
    let roleIdx = -1;
    let start = 0;
    const head = lines[0].split(/[,\t]/).map((s) => s.replace(/^"|"$/g, "").trim());
    if (head.includes("姓名")) {
      nameIdx = head.indexOf("姓名");
      dateIdx = head.indexOf("日期");
      eventIdx = head.indexOf("場次");
      resultIdx = head.indexOf("排班結果");
      roleIdx = head.indexOf("身分別");
      start = 1;
    }
    const next: OverrideMap = { ...overrides };
    let applied = 0;
    const misses: string[] = [];
    for (let i = start; i < lines.length; i++) {
      const cells = lines[i].split(/[,\t]/).map((s) => s.replace(/^"|"$/g, "").trim());
      const name = cells[nameIdx] ?? "";
      const date = dateIdx >= 0 ? cells[dateIdx] ?? "" : "";
      const key = eventIdx >= 0 ? cells[eventIdx] ?? "" : "";
      const resRaw = resultIdx >= 0 ? cells[resultIdx] ?? "" : "";
      const roleRaw = roleIdx >= 0 ? cells[roleIdx] ?? "" : "";
      const res: "in" | "wait" | null = /入選|正取|in/i.test(resRaw)
        ? "in"
        : /遞補|備取|wait/i.test(resRaw)
          ? "wait"
          : null;
      const vId = nameToId.get(name);
      if (!vId || !res || !key) {
        misses.push(lines[i]);
        continue;
      }
      const matched = events.filter(
        (e) =>
          (!date || formatDate(e.eventDate) === date || e.eventDate === date) &&
          (e.title === key || e.title.includes(key)),
      );
      if (!matched.length) {
        misses.push(lines[i]);
        continue;
      }
      // 身分別欄有值就用它指定的區；沒有就套用兩區（純種子 / 純志工只有一區會生效）
      const buckets: RoleBucket[] = /種子/.test(roleRaw)
        ? ["seed"]
        : /志工/.test(roleRaw)
          ? ["activity"]
          : ["seed", "activity"];
      for (const e of matched) {
        for (const b of buckets) {
          next[`${e.id}:${vId}:${b}`] = res;
        }
        applied++;
      }
    }
    setOverrides(next);
    setImportMsg(
      `已套用 ${applied} 筆覆寫${misses.length ? `；${misses.length} 行對不到（姓名 / 場次 / 結果欄請檢查）` : ""}`,
    );
  }

  async function clearOverrides() {
    setOverrides({});
    setImportMsg("已清除所有手動調整，回到「建議排班」版。");
    setImportText("");
    // 明確把共用覆寫清空（空的覆寫不會被自動存，所以這裡要主動送一次）
    try {
      setSyncState("saving");
      const res = await adminSaveScheduleOverrides({});
      setSavedAt(res.at);
      setSyncState("idle");
    } catch {
      setSyncState("error");
    }
  }


  // 【分散】同一「場次類型」（同校同節整季 / 星星國視為一組）把入選名額平均分給有填的人：
  //  每輪挑被排最少的人 → 未達標優先 → 填單順位。不製造新缺口。回傳新的覆寫表 + 調整數。
  function computeSpread(baseOvr: OverrideMap): { next: OverrideMap; moved: number } {
    const next: OverrideMap = { ...baseOvr };
    const groupKey = (e: AdminEvent) =>
      e.category === "charity" ? "charity" : `${e.locationKey}|${e.title}`;
    const groups = new Map<string, AdminEvent[]>();
    for (const e of events) {
      if (!e.isOpen) continue;
      const k = groupKey(e);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(e);
    }
    const hoursMap = hoursMapFor(baseOvr);
    let moved = 0;
    for (const [, evs] of groups) {
      evs.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
      for (const bucket of ["seed", "activity"] as RoleBucket[]) {
        const count = new Map<number, number>();
        const cp = (e: AdminEvent) => (bucket === "seed" ? e.capacitySeed : e.capacityActivity);
        let poolSize = 0;
        const seen = new Set<number>();
        for (const e of evs)
          for (const r of splitByBucketWith(e.id, cp(e), bucket, next))
            if (!seen.has(r.id)) {
              seen.add(r.id);
              poolSize++;
            }
        for (const e of evs) {
          const cap = cp(e) ?? Number.MAX_SAFE_INTEGER;
          const cands = splitByBucketWith(e.id, cp(e), bucket, next);
          if (cands.length === 0 || poolSize <= cap) continue;
          const ranked = [...cands].sort(
            (a, b) =>
              (count.get(a.id) ?? 0) - (count.get(b.id) ?? 0) ||
              Number((hoursMap.get(a.name) ?? 0) >= MIN_HOURS) -
                Number((hoursMap.get(b.name) ?? 0) >= MIN_HOURS) ||
              a.seq - b.seq,
          );
          const takeIds = new Set(ranked.slice(0, cap).map((r) => r.id));
          const other = bucket === "seed" ? "activity" : "seed";
          for (const r of cands) {
            const want = takeIds.has(r.id) ? "in" : "wait";
            const k = `${e.id}:${r.id}:${bucket}`;
            if ((next[k] ?? (r.isIn ? "in" : "wait")) !== want) moved++;
            next[k] = want;
            if (want === "in") next[`${e.id}:${r.id}:${other}`] = "wait";
          }
          for (const id of takeIds) count.set(id, (count.get(id) ?? 0) + 1);
        }
      }
    }
    return { next, moved };
  }

  // 【讓賢】讓未達 4h 的遞補者進入入選：有空名額直接補（同時填缺），沒空位才由已達 4h、讓出後仍 ≥ 4h 的人讓位。
  function computeYield(baseOvr: OverrideMap): {
    next: OverrideMap;
    filled: number;
    swaps: number;
  } {
    const next: OverrideMap = { ...baseOvr };
    const localHours = hoursMapFor(baseOvr);
    let filled = 0;
    let swaps = 0;
    for (const e of events) {
      if (!e.isOpen) continue;
      const placed = new Set<number>();
      for (const bucket of ["seed", "activity"] as RoleBucket[]) {
        const cap = bucket === "seed" ? e.capacitySeed : e.capacityActivity;
        const rows = splitByBucketWith(e.id, cap, bucket, next);
        const capN = cap ?? rows.length;
        const other = bucket === "seed" ? "activity" : "seed";
        let inList = rows.filter((r) => r.isIn).map((r) => ({ id: r.id, name: r.name }));
        const takers = rows
          .filter(
            (r) => !r.isIn && !placed.has(r.id) && (localHours.get(r.name) ?? 0) < MIN_HOURS,
          )
          .sort(
            (a, b) =>
              (localHours.get(a.name) ?? 0) - (localHours.get(b.name) ?? 0) || a.seq - b.seq,
          );
        for (const taker of takers) {
          const putIn = () => {
            next[`${e.id}:${taker.id}:${bucket}`] = "in";
            next[`${e.id}:${taker.id}:${other}`] = "wait";
            localHours.set(taker.name, (localHours.get(taker.name) ?? 0) + e.creditHours);
            inList.push({ id: taker.id, name: taker.name });
            placed.add(taker.id);
          };
          if (inList.length < capN) {
            putIn();
            filled++;
            continue;
          }
          const giver = inList
            .filter((g) => (localHours.get(g.name) ?? 0) - e.creditHours >= MIN_HOURS)
            .sort((a, b) => (localHours.get(b.name) ?? 0) - (localHours.get(a.name) ?? 0))[0];
          if (!giver) break;
          next[`${e.id}:${giver.id}:${bucket}`] = "wait";
          localHours.set(giver.name, (localHours.get(giver.name) ?? 0) - e.creditHours);
          inList = inList.filter((g) => g.id !== giver.id);
          putIn();
          swaps++;
        }
      }
    }
    return { next, filled, swaps };
  }

  // 預設就是「建議排班」：純先到先得 → 分散 → 讓每人達標＋填缺。
  // 這是自動算出來的基準（不寫進資料庫），手動調整 / 匯入疊在它上面。
  const recommendedMap = useMemo(
    () => computeYield(computeSpread({}).next).next,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, rosters, rolesByName],
  );
  const effectiveOverrides = useMemo(
    () => ({ ...recommendedMap, ...overrides }),
    [recommendedMap, overrides],
  );
  function splitByBucket(eventId: number, cap: number | null, bucket: RoleBucket) {
    return splitByBucketWith(eventId, cap, bucket, effectiveOverrides).map((r) => ({
      ...r,
      // ✎ 只標「手動」調整（本地 overrides），不標建議排班的自動結果
      overridden: overrides[`${eventId}:${r.id}:${bucket}`] !== undefined,
    }));
  }

  // 每位學生「目前已排時數」——與排班（含手動微調 / 匯入）連動。
  // 雙身分的人可能同時出現在種子區與志工區，但一場只算一次（isIn 任一區為真＝入選這場）。
  const hoursByVolunteer = useMemo(() => {
    type EvRef = { label: string; hours: number };
    type Row = {
      name: string;
      bucket: RoleBucket;
      hours: number;
      picks: number;
      selectedHours: number;
      dual: boolean;
      inEvents: EvRef[];
      waitEvents: EvRef[];
    };
    const m = new Map<number, Row>();
    for (const e of events) {
      // 這場每位有勾選的人：是否在任一區入選
      const perVol = new Map<number, { name: string; bucket: RoleBucket; anyIn: boolean }>();
      for (const bucket of ["seed", "activity"] as RoleBucket[]) {
        const cap = bucket === "seed" ? e.capacitySeed : e.capacityActivity;
        for (const r of splitByBucket(e.id, cap, bucket)) {
          const p = perVol.get(r.id) ?? { name: r.name, bucket: r.bucket, anyIn: false };
          if (r.isIn) p.anyIn = true;
          perVol.set(r.id, p);
        }
      }
      const ref: EvRef = {
        label: `${formatDate(e.eventDate)}（${e.eventDate ? weekdayOf(e.eventDate) : "—"}）${e.title}`,
        hours: e.creditHours,
      };
      for (const [vid, p] of perVol) {
        const cur: Row =
          m.get(vid) ??
          {
            name: p.name,
            bucket: p.bucket,
            hours: 0,
            picks: 0,
            selectedHours: 0,
            dual: isDual(p.name),
            inEvents: [],
            waitEvents: [],
          };
        cur.selectedHours += e.creditHours; // 有勾選就算（含遞補）＝送單門檻用的數字
        if (p.anyIn) {
          cur.hours += e.creditHours;
          cur.picks += 1;
          cur.inEvents.push(ref);
        } else {
          cur.waitEvents.push(ref);
        }
        m.set(vid, cur);
      }
    }
    return [...m.values()].sort((a, b) => a.hours - b.hours || a.name.localeCompare(b.name));
  }, [events, rosters, overrides, rolesByName]);

  const overrideCount = Object.keys(overrides).length;

  const hoursLookup = useMemo(
    () => new Map(hoursByVolunteer.map((v) => [v.name, v])),
    [hoursByVolunteer],
  );

  // 目前缺人的場次（入選人數 < 名額）
  const gaps = useMemo(() => {
    type Waiter = {
      name: string;
      seq: number;
      roleLabel: string;
      canSeed: boolean; // 能補種子缺
      canAct: boolean; // 能補志工缺
      hours: number; // 目前已排時數
      met: boolean; // 已達門檻
    };
    const out: Array<{
      id: number;
      label: string;
      location: string | null;
      seedGap: number;
      actGap: number;
      enrolled: Array<{ name: string; roleLabel: string }>; // 這個場次目前已入選的人
      waiters: Waiter[];
    }> = [];
    for (const e of events) {
      if (!e.isOpen) continue;
      if (!activeFilter.match(e)) continue; // 跟上方「篩選場次」連動
      const seedRows = splitByBucket(e.id, e.capacitySeed, "seed");
      const actRows = splitByBucket(e.id, e.capacityActivity, "activity");
      const seedIn = seedRows.filter((r) => r.isIn).length;
      const actIn = actRows.filter((r) => r.isIn).length;
      const seedGap = e.capacitySeed == null ? 0 : Math.max(0, e.capacitySeed - seedIn);
      const actGap = e.capacityActivity == null ? 0 : Math.max(0, e.capacityActivity - actIn);
      if (seedGap === 0 && actGap === 0) continue;
      // 已入選（已預排）的人 —— 只當背景資訊，不算「可協調名單」
      const enrolledSet = new Set<string>();
      const enrolled: Array<{ name: string; roleLabel: string }> = [];
      for (const r of seedRows.filter((r) => r.isIn)) {
        if (!enrolledSet.has(r.name.trim())) {
          enrolledSet.add(r.name.trim());
          enrolled.push({ name: r.name, roleLabel: "種子教師" });
        }
      }
      for (const r of actRows.filter((r) => r.isIn)) {
        if (!enrolledSet.has(r.name.trim())) {
          enrolledSet.add(r.name.trim());
          enrolled.push({ name: r.name, roleLabel: "活動志工" });
        }
      }
      // 可協調的人：一個人一列（依姓名去重）。
      //  排除：① 已在本場入選　② 時數已達 4h（已排滿＝已預排）——這兩種都不算「需要協調的名單」。
      const wm = new Map<string, Waiter>();
      const addWaiter = (r: { name: string; seq: number }, canSeed: boolean, canAct: boolean) => {
        const nm = r.name.trim();
        if (enrolledSet.has(nm)) return;
        if ((hoursLookup.get(r.name)?.hours ?? 0) >= MIN_HOURS) return; // 時數已滿的不列
        const cur = wm.get(nm);
        if (cur) {
          cur.canSeed = cur.canSeed || canSeed;
          cur.canAct = cur.canAct || canAct;
          cur.seq = Math.min(cur.seq, r.seq);
          return;
        }
        const h = hoursLookup.get(r.name);
        const d = isDual(r.name);
        wm.set(nm, {
          name: r.name,
          seq: r.seq,
          roleLabel: d ? "雙身分" : canSeed ? "英文種子教師" : "活動志工",
          canSeed,
          canAct,
          hours: h?.hours ?? 0,
          met: (h?.hours ?? 0) >= MIN_HOURS,
        });
      };
      if (seedGap > 0)
        for (const r of seedRows.filter((r) => !r.isIn)) addWaiter(r, true, isDual(r.name));
      if (actGap > 0)
        for (const r of actRows.filter((r) => !r.isIn)) addWaiter(r, isDual(r.name), true);
      const waiters = [...wm.values()].sort(
        (a, b) => Number(a.met) - Number(b.met) || a.seq - b.seq,
      );
      out.push({
        id: e.id,
        label: `${formatDate(e.eventDate)}（${e.eventDate ? weekdayOf(e.eventDate) : "—"}）${e.title}`,
        location: e.location,
        seedGap,
        actGap,
        enrolled,
        waiters,
      });
    }
    return out;
  }, [events, rosters, overrides, rolesByName, hoursLookup, tab]);

  // 同時具兩身分、且目前未達門檻的人 —— 缺人時可優先協調
  const dualUnderMin = useMemo(
    () =>
      hoursByVolunteer.filter((v) => v.dual && v.hours < MIN_HOURS).map((v) => v.name),
    [hoursByVolunteer],
  );

  function exportCsv() {
    const headers = [
      "月份",
      "日期",
      "星期",
      "場次",
      "地點",
      "身分別",
      "排班結果",
      "填單順位",
      "姓名",
      "Email",
      "電話",
      "填單狀態",
    ];
    const lines: string[][] = [];
    for (const e of shownEvents) {
      for (const bucket of ["seed", "activity"] as RoleBucket[]) {
        const cap = bucket === "seed" ? e.capacitySeed : e.capacityActivity;
        const group = splitByBucket(e.id, cap, bucket);
        for (const r of group) {
          lines.push([
            monthLabel(e.monthGroup),
            formatDate(e.eventDate),
            e.eventDate ? weekdayOf(e.eventDate) : "",
            e.title,
            e.location ?? "",
            BUCKET_LABEL[bucket],
            r.isIn ? "入選" : "遞補",
            String(r.seq),
            r.name,
            r.primaryEmail,
            r.phone,
            r.status === "submitted" ? "已確認" : "草稿",
          ]);
        }
      }
    }
    const esc = (s: string) => `"${(s ?? "").replace(/"/g, '""')}"`;
    const csv =
      "﻿" + [headers, ...lines].map((row) => row.map(esc).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `排班_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
        <button className="btn ghost sm" onClick={load} disabled={loading}>
          {loading ? "更新中…" : "↻ 重新整理最新填單資料"}
        </button>
        <button className="btn primary sm" onClick={exportCsv} disabled={loading || !events.length}>
          匯出排班 CSV
        </button>
      </div>

      {/* 場次篩選 —— 置頂跟著捲動；同時作用於「各場次卡」「目前缺人的場次」「匯出」 */}
      <div
        id="sec-filter"
        style={{
          position: "sticky",
          top: 49, // 卡在最上方「管理後台」sticky 列的下面
          zIndex: 10,
          scrollMarginTop: 110,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          margin: "10px -4px 8px",
          padding: "10px 12px",
          border: "1px solid var(--line)",
          borderRadius: 10,
          background: "#fff",
          boxShadow: "0 4px 12px rgba(20,32,92,0.08)",
        }}
      >
        <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>
          篩選場次：
        </span>
        {LOCATION_TABS.map((t) => (
          <button
            key={t.key}
            className={`btn sm ${tab === t.key ? "primary" : "ghost"}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            border: "1px solid var(--line)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          {(
            [
              ["overview", "總覽"],
              ["sessions", "各場次名單"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              style={{
                border: "none",
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 800,
                cursor: "pointer",
                background: view === k ? "var(--ink)" : "#fff",
                color: view === k ? "#fff" : "var(--ink-soft)",
              }}
            >
              {label}
              {k === "sessions" && tab !== "all" ? `（${activeFilter.label}）` : ""}
            </button>
          ))}
        </span>
      </div>

      {view === "sessions" && (
        <div className="muted" style={{ fontSize: 12, margin: "6px 0 10px" }}>
          以下每張卡＝一個時段的實際名單，直接點標籤即可微調（受上方「篩選場次」影響）。缺人協調、學生時數、排班原則請切回「總覽」。
        </div>
      )}

      {view === "overview" && (
        <>
      <div
        className="muted"
        style={{
          fontSize: 12,
          lineHeight: 1.9,
          margin: "8px 0 12px",
          padding: "10px 14px",
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: 10,
        }}
      >
        <div style={{ fontWeight: 700, color: "var(--ink-soft)", marginBottom: 4 }}>
          這一頁怎麼看
        </div>
        <div>
          ・<strong>預設畫面＝建議排班</strong>：先驗證身分（種子缺找種子、志工缺找志工、雙身分兩種都行）→ 送出時間早的入選、其餘遞補 → 自動把重複的人分散、把未達 {MIN_HOURS}h 的人補進空名額 / 由已達標者讓位。
        </div>
        <div>
          ・<strong>雙身分</strong>的人同時出現在「英文種子教師」與「活動志工」兩區（志工區標「雙身分・跨區」）；在一區排入，另一區自動退遞補，一場只算一次時數。
        </div>
        <div>
          ・點「入選／遞補」標籤可<strong>手動微調</strong>；改動自動存回系統、所有管理員共用（不動志工與場次資料）。「全部清除」＝回到建議排班版。
        </div>
        <div>
          ・完整 4 條原則見本頁最下方。
          {loadedAt && `　填單資料最後更新 ${loadedAt.toLocaleTimeString("zh-TW", { hour12: false })}`}
          {syncState === "saving" && <span style={{ marginLeft: 6 }}>儲存中…</span>}
          {syncState === "error" && (
            <span className="tag warn" style={{ marginLeft: 6 }}>
              存檔失敗，請重試
            </span>
          )}
          {overrideCount > 0 && (
            <>
              {"　"}
              <span className="tag warn">已有 {overrideCount} 筆手動調整</span>
              {savedAt && syncState === "idle" && (
                <span style={{ marginLeft: 4 }}>
                  （已同步 {new Date(savedAt).toLocaleString("zh-TW", { hour12: false })}）
                </span>
              )}
              <button
                className="btn ghost sm"
                style={{ marginLeft: 6, padding: "1px 8px" }}
                onClick={clearOverrides}
              >
                全部清除
              </button>
            </>
          )}
        </div>
      </div>

      {err && <div className="alert err">{err}</div>}

      {/* 每位學生目前已排時數（連動排班）—— 收合 */}
      {!loading && hoursByVolunteer.length > 0 && (
        <div id="sec-hours" className="card" style={{ marginBottom: 12, scrollMarginTop: 48 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexWrap: "wrap" }}
            onClick={() => setShowHours((s) => !s)}
          >
            <strong>每位學生目前已排時數</strong>
            <span className="muted" style={{ fontSize: 12 }}>
              目標：每個時段人數到齊；其次每人滿 {MIN_HOURS} 小時
            </span>
            <span className="muted" style={{ fontSize: 12 }}>
              · {hoursByVolunteer.filter((v) => v.hours < MIN_HOURS).length} 人未達 {MIN_HOURS}h
            </span>
            <span style={{ marginLeft: "auto" }}>{showHours ? "▲" : "▼"}</span>
          </div>
          {showHours && (
          <>
          <div className="table-wrap" style={{ marginTop: 8 }}>
            <table className="grid" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>姓名</th>
                  <th>身分別（名額計算）</th>
                  <th>入選場次數</th>
                  <th>勾選總時數</th>
                  <th>已排時數（入選）</th>
                  <th>是否達標</th>
                </tr>
              </thead>
              <tbody>
                {hoursByVolunteer.map((v) => {
                  const open = expandedVol === v.name;
                  return (
                    <Fragment key={v.name}>
                      <tr>
                        <td>
                          {v.name}
                          {v.dual && (
                            <span className="tag" style={{ marginLeft: 4 }}>
                              雙身分
                            </span>
                          )}
                        </td>
                        <td>{BUCKET_LABEL[v.bucket]}</td>
                        <td>
                          <button
                            className="btn ghost sm"
                            style={{ padding: "1px 8px" }}
                            disabled={v.picks === 0}
                            onClick={() => setExpandedVol(open ? null : v.name)}
                          >
                            {v.picks} {v.picks > 0 ? (open ? "▲" : "▼") : ""}
                          </button>
                        </td>
                        <td>
                          <button
                            className="btn ghost sm"
                            style={{ padding: "1px 8px" }}
                            disabled={v.selectedHours === 0}
                            onClick={() => setExpandedVol(open ? null : v.name)}
                          >
                            {v.selectedHours} h {v.selectedHours > 0 ? (open ? "▲" : "▼") : ""}
                          </button>
                        </td>
                        <td>
                          <strong>{v.hours}</strong> h
                        </td>
                        <td>
                          {v.hours >= MIN_HOURS ? (
                            <span className="tag ok">達 {MIN_HOURS}h</span>
                          ) : (
                            <span className="tag warn">
                              差 {Math.round((MIN_HOURS - v.hours) * 10) / 10}h
                            </span>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={6} style={{ background: "var(--bg)" }}>
                            <div style={{ fontSize: 12, lineHeight: 1.8, padding: "4px" }}>
                              <strong>入選（{v.hours}h）：</strong>
                              {v.inEvents.length === 0 ? (
                                <span className="muted"> 目前一場都沒入選</span>
                              ) : (
                                <ul style={{ margin: "2px 0 6px", paddingLeft: 18 }}>
                                  {v.inEvents.map((ie, i) => (
                                    <li key={i}>
                                      {ie.label} · {ie.hours}h
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {v.waitEvents.length > 0 && (
                                <>
                                  <strong style={{ color: "var(--warn-ink)" }}>
                                    遞補中（{v.selectedHours - v.hours}h，尚未排進去）：
                                  </strong>
                                  <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
                                    {v.waitEvents.map((ie, i) => (
                                      <li key={i}>
                                        {ie.label} · {ie.hours}h
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 11,
              lineHeight: 1.8,
              color: "var(--ink-faint)",
              marginTop: 8,
            }}
          >
            <strong style={{ color: "var(--ink-soft)" }}>「未達 {MIN_HOURS}h」不代表這個人不能送單。</strong>
            <br />
            送單門檻檢查的是「<strong>勾選總時數</strong>」＝學生勾的所有場次（含之後被排到遞補的），這一欄每個人都已 ≥ {MIN_HOURS}h、才送得出來。
            <br />
            這裡的「<strong>已排時數</strong>」只算「入選」的場次、遞補不計。若某人選的場次都排在遞補（例：星星國種子每場只有 1 名額，較晚送的人全被卡在遞補），已排時數就會是 0，顯示「差 {MIN_HOURS}h」。
            <br />
            預設畫面已自動套用建議排班（分散＋補進空缺＋讓位），還未達標＝真的沒位子。要處理：① 在下方場次卡手動把他切成「入選」　② 到「場次管理」把該場名額 +1　③ 看「目前缺人的場次」的協調名單、請對應學生補選。
            <br />
            點「入選場次數」或「勾選總時數」可展開，看他哪幾場入選、哪幾場遞補。
          </div>
          </>
          )}
        </div>
      )}

      {/* 目前缺人的場次（需協調排班）—— 收合，置於時數表下方 */}
      {!loading && events.length > 0 && (
        <div id="sec-gaps" className="card" style={{ marginBottom: 12, scrollMarginTop: 48 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexWrap: "wrap" }}
            onClick={() => setShowGaps((s) => !s)}
          >
            <strong>目前缺人的場次（需協調排班）</strong>
            {tab !== "all" && <span className="tag">篩選：{activeFilter.label}</span>}
            <span className="muted" style={{ fontSize: 12 }}>
              {gaps.length === 0
                ? tab === "all"
                  ? "目前每一節都有人 ✅"
                  : `「${activeFilter.label}」目前每一節都有人 ✅`
                : `${gaps.length} 個場次尚缺人`}
            </span>
            <span style={{ marginLeft: "auto" }}>{showGaps ? "▲" : "▼"}</span>
          </div>
          {showGaps && gaps.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div className="table-wrap">
                <table className="grid" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th>場次</th>
                      <th>地點</th>
                      <th>缺種子</th>
                      <th>缺志工</th>
                      <th>可協調的人</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gaps.map((g) => {
                      const go = expandedGap === g.id;
                      return (
                        <Fragment key={g.id}>
                          <tr>
                            <td>
                              <button
                                className="btn ghost sm"
                                style={{ padding: "1px 6px", fontSize: 12, textAlign: "left" }}
                                title="跳到下方這一場的排班名單"
                                onClick={() => jumpToEvent(g.id)}
                              >
                                {g.label} ↓
                              </button>
                            </td>
                            <td className="muted">{g.location ?? "—"}</td>
                            <td>{g.seedGap > 0 ? <span className="tag warn">缺 {g.seedGap}</span> : "—"}</td>
                            <td>{g.actGap > 0 ? <span className="tag warn">缺 {g.actGap}</span> : "—"}</td>
                            <td>
                              <button
                                className="btn ghost sm"
                                style={{ padding: "1px 8px" }}
                                onClick={() => setExpandedGap(go ? null : g.id)}
                              >
                                {g.waiters.length === 0
                                  ? "看名單"
                                  : `${g.waiters.length} 人可協調`}{" "}
                                {go ? "▲" : "▼"}
                              </button>
                            </td>
                          </tr>
                          {go && (
                            <tr>
                              <td colSpan={5} style={{ background: "var(--bg)" }}>
                                <div style={{ fontSize: 12, lineHeight: 1.8, padding: "4px" }}>
                                  <span className="muted">
                                    已入選（不列入協調名單）：
                                    {g.enrolled.length === 0
                                      ? "尚無人入選"
                                      : g.enrolled.map((p) => `${p.name}（${p.roleLabel}）`).join("、")}
                                  </span>
                                  <div style={{ fontWeight: 700, marginTop: 4 }}>
                                    可協調的人（在這場勾選過、目前排遞補、身分符合、時數未滿 {MIN_HOURS}h）
                                  </div>
                                  {g.waiters.length === 0 ? (
                                    <div className="muted" style={{ paddingLeft: 4 }}>
                                      沒有符合的人——須請學生補選這一場，或由主辦指派。
                                    </div>
                                  ) : (
                                    <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>
                                      {g.waiters.map((w, i) => (
                                        <li key={i}>
                                          順位 {w.seq}｜<strong>{w.name}</strong>｜{w.roleLabel}｜可補
                                          {[
                                            w.canSeed && g.seedGap > 0 ? "種子缺" : null,
                                            w.canAct && g.actGap > 0 ? "志工缺" : null,
                                          ]
                                            .filter(Boolean)
                                            .join("、")}
                                          ｜目前已排 {w.hours}h
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                此清單跟上方「篩選場次」連動（目前：{activeFilter.label}）。
                「可協調的人」＝在這一場勾選過、目前排在遞補、身分對得上、且時數還沒滿 {MIN_HOURS}h 的人（已入選、時數已滿的人不列）。
                名單為空＝這一場沒有可協調的人，須請學生補選、由主辦指派，或到「場次管理」加名額 / 在下方場次卡手動排入。
                {dualUnderMin.length > 0 && (
                  <>
                    {" "}目前雙身分且未達 {MIN_HOURS}h：{dualUnderMin.join("、")}——可請他們補選缺人場次。
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      )}
      {/* 手動匯入 / 覆寫排班 */}
      {!loading && events.length > 0 && (
        <div id="sec-import" className="card" style={{ marginBottom: 12, scrollMarginTop: 48 }}>
          <div
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => setShowImport((s) => !s)}
          >
            <strong>手動匯入 / 覆寫排班</strong>
            <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
              （進階：批次貼上調整，改共用排班覆寫，不動志工填單與場次資料）
            </span>
            <span style={{ marginLeft: "auto" }}>{showImport ? "▲" : "▼"}</span>
          </div>
          {showImport && (
            <div style={{ marginTop: 10 }}>
              <textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                rows={6}
                placeholder={
                  "每行一筆，四欄、逗號或 Tab 分隔：姓名, 日期, 場次關鍵字, 入選/遞補\n" +
                  "日期可留空，但逗號要留：曾逸嘉,,稻香,入選\n" +
                  "指定日期：曾逸嘉, 2026-09-10, 稻香, 入選\n" +
                  "也可直接貼回本頁「匯出排班 CSV」的內容（會自動抓表頭）"
                }
                style={{
                  width: "100%",
                  fontSize: 12,
                  fontFamily: "inherit",
                  padding: 8,
                  border: "1px solid var(--line)",
                  borderRadius: 8,
                }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                <button className="btn primary sm" onClick={applyImport}>
                  套用覆寫
                </button>
                <button className="btn ghost sm" onClick={clearOverrides}>
                  清除所有手動調整
                </button>
                {importMsg && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    {importMsg}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
        </>
      )}

      {view === "sessions" && !loading && shownEvents.length > 0 && (
        <div id="sec-events" style={{ margin: "6px 0 14px", scrollMarginTop: 110 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "var(--ink)",
                background: "#fff",
                border: "1px solid var(--line)",
                borderRadius: 999,
                padding: "6px 16px",
                whiteSpace: "nowrap",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
            >
              各場次排班名單
            </span>
            <span style={{ flex: 1, height: 1, background: "var(--line)" }} />
            <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {tab === "all"
                ? `共 ${shownEvents.length} 場`
                : `「${activeFilter.label}」${shownEvents.length} 場`}
            </span>
          </div>
        </div>
      )}
      {view === "sessions" &&
        (loading && !events.length ? (
        <p>載入中…</p>
      ) : shownEvents.length === 0 ? (
        <p className="muted center">此篩選範圍沒有場次</p>
      ) : (
        shownEvents.map((e) => {
          const seed = splitByBucket(e.id, e.capacitySeed, "seed");
          const activity = splitByBucket(e.id, e.capacityActivity, "activity");
          const total = new Set([...seed, ...activity].map((r) => r.id)).size;
          return (
            <div
              className="card"
              key={e.id}
              id={`sched-event-${e.id}`}
              style={{
                marginBottom: 12,
                scrollMarginTop: 110, // 讓出上方 sticky 列（管理後台 + 篩選場次）的高度
                ...(highlightedEvent === e.id
                  ? { outline: "2px solid #1a7a3c", outlineOffset: 2 }
                  : {}),
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <strong>{formatDate(e.eventDate)}</strong>
                {e.eventDate && <span className="muted">（{weekdayOf(e.eventDate)}）</span>}
                <span>{e.title}</span>
                {e.location && <span className="muted">· {e.location}</span>}
                <span className="muted">
                  · {e.startTime || e.endTime ? `${e.startTime ?? ""}–${e.endTime ?? ""}` : "時間未定"}
                </span>
                <span className="muted">· {e.creditHours}h</span>
                <span className="muted">
                  · 名額 種子 {e.capacitySeed ?? "不限"}＋志工 {e.capacityActivity ?? "不限"}
                </span>
                {!e.isOpen && <span className="tag muted">已關閉</span>}
                <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
                  勾選 {total} 人
                </span>
              </div>
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "1fr", marginTop: 8 }}>
                <BucketBlock
                  title={`英文種子教師（名額 ${e.capacitySeed ?? "不限"}）`}
                  rows={seed}
                  onToggle={(vid, cur) => toggle(e.id, vid, cur, "seed")}
                />
                <BucketBlock
                  title={`活動志工（名額 ${e.capacityActivity ?? "不限"}）`}
                  rows={activity}
                  onToggle={(vid, cur) => toggle(e.id, vid, cur, "activity")}
                />
              </div>
            </div>
          );
        })
        ))}

      {/* 排班邏輯原則 */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>排班邏輯原則</h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>
          <strong>最終目標：每一個時段的種子教師 / 活動志工人數都到齊。</strong>
          以下 4 條是「達成這個目標時、決定誰排誰」的順序。
        </p>
        <div style={{ display: "grid", gap: 14 }}>
          {[
            {
              t: "先驗證身分",
              d: "種子教師的缺只能由具「英文種子教師」身分者遞補、活動志工的缺只能由「活動志工」遞補；同時勾選兩種身分的人可以補任一種缺。身分不符者不列入這一場的遞補人選。",
            },
            {
              t: "同一時段多人填寫，以送出時間早的為主",
              d: "通過身分驗證後，同一名額多人競爭時以調查表送出時間排序：先送者入選，其餘依序列為遞補 1、2、3⋯。",
            },
            {
              t: `讓每位同學都達到預期時數（${MIN_HOURS} 小時），排班儘量不重複`,
              d: `目標是每人累計滿 ${MIN_HOURS} 小時（時數只看總量、不分身分）。預設畫面已自動：把同一類型活動裡重複的人分散開，再把未達標的人放進空名額（同時填缺）、沒空名額才由已達 ${MIN_HOURS}h、讓出後仍 ≥ ${MIN_HOURS}h 的人讓位。填單人數太少時無法完全避免重複、也無法讓每個人都滿 ${MIN_HOURS}h。`,
            },
            {
              t: "目標：每一場都有種子教師或活動志工可以參加",
              d: "這是最終目標——每一個時段的種子教師 / 活動志工人數都要到齊。缺額場次只要有學生勾選即直接錄取（名額沒滿時，有填的人都是入選）。仍有空缺時，看「目前缺人的場次」的協調名單去補；完全沒人選過的場次須請學生補選或由主辦指派。",
            },
          ].map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span
                style={{
                  flex: "0 0 auto",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "var(--ink)",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {i + 1}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>{p.t}</div>
                <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
                  {p.d}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: "1px solid var(--line)",
            fontSize: 12,
            lineHeight: 1.7,
            color: "var(--ink-soft)",
          }}
        >
          補充：同時錄取「英文種子教師」與「活動志工」的人，排班時可依缺額身分別填補；但只能排進他實際在調查表勾選過的場次——沒選過的場次（例如整季無人填的節次）不會自動排入。
        </div>
      </div>

      {/* 資料來源（小字灰底） */}
      <div
        style={{
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: 10,
          padding: "10px 14px",
          fontSize: 11,
          lineHeight: 1.7,
          color: "var(--ink-faint)",
        }}
      >
        <strong style={{ color: "var(--ink-soft)" }}>資料來源方式</strong>
        <br />
        ・場次時間、名額、勾選人數與入選／遞補：即時讀取本系統資料庫（唯讀，不修改任何資料）。
        <br />
        ・志工可服務時段：以線上出席調查表填答為主，另參酌 2026-09-02「學生服務時間調查」補充表；補充表中「待確認」欄位，以各志工 9/7、9/10 回覆為準。
        <br />
        ・本頁僅供排班參考，不代表最終認證時數；實際認列以主辦核定為準。
      </div>
    </div>
  );
}

function BucketBlock({
  title,
  rows,
  onToggle,
}: {
  title: string;
  rows: Array<{
    id: number;
    name: string;
    primaryEmail: string;
    phone: string;
    status: string;
    seq: number;
    isIn: boolean;
    overridden: boolean;
    waitlistPosition: number;
    crossListed: boolean;
  }>;
  onToggle: (volunteerId: number, currentIsIn: boolean) => void;
}) {
  return (
    <div>
      <div className="muted" style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>
          尚無志工勾選
        </div>
      ) : (
        <table className="grid" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: 44 }}>順位</th>
              <th style={{ width: 108 }}>排班（可點）</th>
              <th>姓名</th>
              <th>Email</th>
              <th>電話</th>
              <th style={{ width: 64 }}>狀態</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              return (
                <tr key={r.id} style={r.crossListed ? { background: "#fafbff" } : undefined}>
                  <td>{r.seq}</td>
                  <td>
                    <button
                      onClick={() => onToggle(r.id, r.isIn)}
                      title="點一下切換入選／遞補（會存成手動調整）"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        minWidth: 74,
                        justifyContent: "center",
                        padding: "5px 12px",
                        fontSize: 13,
                        fontWeight: 800,
                        borderRadius: 999,
                        cursor: "pointer",
                        transition: "transform .05s ease",
                        background: r.isIn ? "#12703a" : "#fff",
                        color: r.isIn ? "#fff" : "#9a5b00",
                        border: r.isIn ? "1px solid #12703a" : "1.5px dashed #e0a94e",
                        boxShadow: r.isIn
                          ? "0 2px 6px rgba(18,112,58,0.30)"
                          : "0 1px 2px rgba(0,0,0,0.04)",
                      }}
                      onMouseDown={(ev) => (ev.currentTarget.style.transform = "scale(0.96)")}
                      onMouseUp={(ev) => (ev.currentTarget.style.transform = "scale(1)")}
                      onMouseLeave={(ev) => (ev.currentTarget.style.transform = "scale(1)")}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 999,
                          background: r.isIn ? "#8fe3ab" : "#e0a94e",
                          flex: "0 0 auto",
                        }}
                      />
                      {r.isIn ? "入選" : "遞補"}
                      {r.overridden && <span title="手動調整">✎</span>}
                    </button>
                  </td>
                  <td>
                    {r.name}
                    {r.crossListed && (
                      <span className="tag" style={{ marginLeft: 4 }}>
                        雙身分・跨區
                      </span>
                    )}
                  </td>
                  <td style={{ wordBreak: "break-all" }}>{r.primaryEmail}</td>
                  <td>{r.phone}</td>
                  <td>
                    {r.status === "submitted" ? (
                      <span className="tag ok">已確認</span>
                    ) : (
                      <span className="tag muted">草稿</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
